import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { Component } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { 
  faPlus, 
  faMinus, 
  faSearch, 
  faTimes, 
  faAngleDown, 
  faAngleUp, 
  faAngleLeft, 
  faAngleRight,
  faChevronLeft,
  faChevronRight,
  faBars,
  faTriangleExclamation,
  faGift,
  faBasketShopping,
  faArrowLeft,
  faArrowRight,
  faMagnifyingGlass
} from '@fortawesome/free-solid-svg-icons';
import { 
  faTrashCan, 
  faCircleQuestion, 
  faMessage, 
  faCalendar 
} from '@fortawesome/free-regular-svg-icons';
import { 
  faFacebook, 
  faInstagram, 
  faLinkedin 
} from '@fortawesome/free-brands-svg-icons';

@Component({
  selector: 'app-fontawesome-showcase',
  template: `
    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem;">
      <!-- Navigation Icons -->
      <div style="text-align: center; padding: 1rem; border: 1px solid #ddd; border-radius: 8px;">
        <h5>Navigation</h5>
        <div style="display: flex; gap: 0.5rem; justify-content: center; margin: 0.5rem 0;">
          <fa-icon [icon]="faAngleLeft"></fa-icon>
          <fa-icon [icon]="faAngleRight"></fa-icon>
          <fa-icon [icon]="faAngleUp"></fa-icon>
          <fa-icon [icon]="faAngleDown"></fa-icon>
        </div>
        <div style="display: flex; gap: 0.5rem; justify-content: center; margin: 0.5rem 0;">
          <fa-icon [icon]="faChevronLeft"></fa-icon>
          <fa-icon [icon]="faChevronRight"></fa-icon>
          <fa-icon [icon]="faArrowLeft"></fa-icon>
          <fa-icon [icon]="faArrowRight"></fa-icon>
        </div>
      </div>

      <!-- Action Icons -->
      <div style="text-align: center; padding: 1rem; border: 1px solid #ddd; border-radius: 8px;">
        <h5>Actions</h5>
        <div style="display: flex; gap: 0.5rem; justify-content: center; margin: 0.5rem 0;">
          <fa-icon [icon]="faPlus"></fa-icon>
          <fa-icon [icon]="faMinus"></fa-icon>
          <fa-icon [icon]="faSearch"></fa-icon>
          <fa-icon [icon]="faTimes"></fa-icon>
        </div>
        <div style="display: flex; gap: 0.5rem; justify-content: center; margin: 0.5rem 0;">
          <fa-icon [icon]="faMagnifyingGlass"></fa-icon>
          <fa-icon [icon]="faTrashCan"></fa-icon>
          <fa-icon [icon]="faBasketShopping"></fa-icon>
          <fa-icon [icon]="faBars"></fa-icon>
        </div>
      </div>

      <!-- Status Icons -->
      <div style="text-align: center; padding: 1rem; border: 1px solid #ddd; border-radius: 8px;">
        <h5>Status & Info</h5>
        <div style="display: flex; gap: 0.5rem; justify-content: center; margin: 0.5rem 0;">
          <fa-icon [icon]="faTriangleExclamation" style="color: #F39C12;"></fa-icon>
          <fa-icon [icon]="faCircleQuestion" style="color: #3498DB;"></fa-icon>
          <fa-icon [icon]="faMessage" style="color: #5C0A31;"></fa-icon>
          <fa-icon [icon]="faGift" style="color: #E74C3C;"></fa-icon>
        </div>
        <div style="display: flex; gap: 0.5rem; justify-content: center; margin: 0.5rem 0;">
          <fa-icon [icon]="faCalendar" style="color: #666;"></fa-icon>
        </div>
      </div>

      <!-- Social Icons -->
      <div style="text-align: center; padding: 1rem; border: 1px solid #ddd; border-radius: 8px;">
        <h5>Social Media</h5>
        <div style="display: flex; gap: 0.5rem; justify-content: center; margin: 0.5rem 0;">
          <fa-icon [icon]="faFacebook" style="color: #1877F2;"></fa-icon>
          <fa-icon [icon]="faInstagram" style="color: #E4405F;"></fa-icon>
          <fa-icon [icon]="faLinkedin" style="color: #0A66C2;"></fa-icon>
        </div>
      </div>
    </div>
  `,
  imports: [FontAwesomeModule],
  standalone: true
})
export class FontAwesomeShowcaseComponent {
  // Solid icons
  faPlus = faPlus;
  faMinus = faMinus;
  faSearch = faSearch;
  faTimes = faTimes;
  faAngleDown = faAngleDown;
  faAngleUp = faAngleUp;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faChevronLeft = faChevronLeft;
  faChevronRight = faChevronRight;
  faBars = faBars;
  faTriangleExclamation = faTriangleExclamation;
  faGift = faGift;
  faBasketShopping = faBasketShopping;
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faMagnifyingGlass = faMagnifyingGlass;

  // Regular icons
  faTrashCan = faTrashCan;
  faCircleQuestion = faCircleQuestion;
  faMessage = faMessage;
  faCalendar = faCalendar;

  // Brand icons
  faFacebook = faFacebook;
  faInstagram = faInstagram;
  faLinkedin = faLinkedin;
}

const meta: Meta<FontAwesomeShowcaseComponent> = {
  title: 'Atoms/Icons/FontAwesome',
  component: FontAwesomeShowcaseComponent,
  parameters: {
    docs: {
      description: {
        component: 'FontAwesome icons used throughout the Supervin application. These are vector icons from the FontAwesome library, providing consistent iconography for various UI elements.',
      },
    },
  },
};

export default meta;
type Story = StoryObj<FontAwesomeShowcaseComponent>;

export const AllIcons: Story = {
  render: () => ({
    template: '<app-fontawesome-showcase></app-fontawesome-showcase>',
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-fontawesome-showcase')).toBeTruthy();
  },
};

export const NavigationIcons: Story = {
  render: () => ({
    template: `
      <div style="display: flex; flex-direction: column; gap: 2rem;">
        <div>
          <h4>Angle Icons (for dropdowns and menus)</h4>
          <div style="display: flex; gap: 1rem; align-items: center;">
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <span>Dropdown</span>
              <fa-icon [icon]="faAngleDown" style="color: #5C0A31;"></fa-icon>
            </div>
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <fa-icon [icon]="faAngleUp" style="color: #5C0A31;"></fa-icon>
              <span>Collapse</span>
            </div>
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <fa-icon [icon]="faAngleLeft" style="color: #5C0A31;"></fa-icon>
              <span>Back</span>
            </div>
            <div style="display: flex; align-items: center; gap: 0.5rem;">
              <span>Next</span>
              <fa-icon [icon]="faAngleRight" style="color: #5C0A31;"></fa-icon>
            </div>
          </div>
        </div>
        
        <div>
          <h4>Chevron Icons (for navigation)</h4>
          <div style="display: flex; gap: 1rem; align-items: center;">
            <button class="btn btn-outline-primary" style="display: flex; align-items: center; gap: 0.5rem;">
              <fa-icon [icon]="faChevronLeft"></fa-icon>
              <span>Previous</span>
            </button>
            <button class="btn btn-outline-primary" style="display: flex; align-items: center; gap: 0.5rem;">
              <span>Next</span>
              <fa-icon [icon]="faChevronRight"></fa-icon>
            </button>
          </div>
        </div>
        
        <div>
          <h4>Arrow Icons (for CTAs)</h4>
          <div style="display: flex; gap: 1rem; align-items: center;">
            <button class="btn btn-primary-supervin" style="display: flex; align-items: center; gap: 0.5rem;">
              <fa-icon [icon]="faArrowLeft"></fa-icon>
              <span>Go Back</span>
            </button>
            <button class="btn btn-primary-supervin" style="display: flex; align-items: center; gap: 0.5rem;">
              <span>Continue</span>
              <fa-icon [icon]="faArrowRight"></fa-icon>
            </button>
          </div>
        </div>
      </div>
    `,
    props: {
      faAngleDown,
      faAngleUp,
      faAngleLeft,
      faAngleRight,
      faChevronLeft,
      faChevronRight,
      faArrowLeft,
      faArrowRight
    }
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('fa-icon')).toBeTruthy();
  },
};

export const ActionIcons: Story = {
  render: () => ({
    template: `
      <div style="display: flex; flex-direction: column; gap: 2rem;">
        <div>
          <h4>Quantity Controls</h4>
          <div style="display: flex; align-items: center; gap: 0.5rem; border: 1px solid #ddd; border-radius: 4px; width: fit-content;">
            <button style="border: none; background: none; padding: 0.5rem; display: flex; align-items: center;">
              <fa-icon [icon]="faMinus" style="color: #666;"></fa-icon>
            </button>
            <span style="padding: 0 1rem; border-left: 1px solid #ddd; border-right: 1px solid #ddd;">2</span>
            <button style="border: none; background: none; padding: 0.5rem; display: flex; align-items: center;">
              <fa-icon [icon]="faPlus" style="color: #666;"></fa-icon>
            </button>
          </div>
        </div>
        
        <div>
          <h4>Search Icons</h4>
          <div style="display: flex; gap: 1rem; align-items: center;">
            <div style="position: relative; width: 300px;">
              <input type="text" placeholder="Søg efter vine..." style="width: 100%; padding: 0.5rem 2.5rem 0.5rem 1rem; border: 1px solid #ddd; border-radius: 4px;">
              <fa-icon [icon]="faSearch" style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: #666;"></fa-icon>
            </div>
            <div style="position: relative; width: 300px;">
              <input type="text" value="Barolo" style="width: 100%; padding: 0.5rem 2.5rem 0.5rem 1rem; border: 1px solid #ddd; border-radius: 4px;">
              <fa-icon [icon]="faTimes" style="position: absolute; right: 0.75rem; top: 50%; transform: translateY(-50%); color: #666; cursor: pointer;"></fa-icon>
            </div>
          </div>
        </div>
        
        <div>
          <h4>Shopping Icons</h4>
          <div style="display: flex; gap: 1rem; align-items: center;">
            <button class="btn btn-primary-supervin" style="display: flex; align-items: center; gap: 0.5rem;">
              <fa-icon [icon]="faBasketShopping"></fa-icon>
              <span>Tilføj til kurv</span>
            </button>
            <button class="btn btn-outline-danger" style="display: flex; align-items: center; gap: 0.5rem;">
              <fa-icon [icon]="faTrashCan"></fa-icon>
              <span>Fjern</span>
            </button>
          </div>
        </div>
      </div>
    `,
    props: {
      faMinus,
      faPlus,
      faSearch,
      faTimes,
      faBasketShopping,
      faTrashCan
    }
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('fa-icon')).toBeTruthy();
  },
};

export const SocialIcons: Story = {
  render: () => ({
    template: `
      <div style="background-color: #5C0A31; color: white; padding: 2rem; border-radius: 8px;">
        <h4 style="margin-bottom: 1rem;">Følg os på sociale medier</h4>
        <div style="display: flex; gap: 1rem;">
          <a href="#" style="color: white; font-size: 1.5rem; transition: transform 0.2s;" 
             onmouseover="this.style.transform='scale(1.1)'" 
             onmouseout="this.style.transform='scale(1)'">
            <fa-icon [icon]="faFacebook"></fa-icon>
          </a>
          <a href="#" style="color: white; font-size: 1.5rem; transition: transform 0.2s;"
             onmouseover="this.style.transform='scale(1.1)'" 
             onmouseout="this.style.transform='scale(1)'">
            <fa-icon [icon]="faInstagram"></fa-icon>
          </a>
          <a href="#" style="color: white; font-size: 1.5rem; transition: transform 0.2s;"
             onmouseover="this.style.transform='scale(1.1)'" 
             onmouseout="this.style.transform='scale(1)'">
            <fa-icon [icon]="faLinkedin"></fa-icon>
          </a>
        </div>
      </div>
    `,
    props: {
      faFacebook,
      faInstagram,
      faLinkedin
    }
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('fa-icon')).toBeTruthy();
  },
};
